import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { AddTagButton } from './AddTagButton'
import { EditorDropdown } from './EditorDropdown'
import { TagBadge } from './TagBadge'
import type { TagCellProps } from './TagCell.types'
import { TagEditor } from './TagEditor'
import type { Tag } from './tagging.types'
import { TagMirror } from './TagMirror'
import { TagOverflow } from './TagOverflow'
import { useTagging } from './useTagging'
import { useTaggingTableSelection } from './useTaggingTableSelection'
import { useVisibleTagCount } from './useVisibleTagCount'

export const TagCell = (props: TagCellProps) => {
  const { rowId, tags } = props
  const [isHovered, setIsHovered] = useState(false)
  const intl = useIntl()
  const tagging = useTagging()
  const selection = useTaggingTableSelection()
  const isReadMode = tagging.mode === 'read'
  const isEditMode = tagging.mode === 'edit'
  const hasTags = tags.length > 0

  const { containerRef, visibleCount, isMeasuring } = useVisibleTagCount(
    isReadMode && hasTags ? tags.length : 0,
  )

  useEffect(() => {
    if (isReadMode) {
      setIsHovered(false)
    }
  }, [isReadMode])

  const isActiveEditorRow = tagging.activeEditorRowId === rowId
  const isChecked = selection.isRowSelected(rowId)
  const showMirror =
    isEditMode && isChecked && !isActiveEditorRow && tagging.isEditorOpen

  const handleAddClick = () => {
    tagging.enterEditMode(rowId)
  }

  const handleTagNameClick = () => {
    if (tagging.isAddingTag || tagging.isRemovingTag) {
      return
    }

    if (isReadMode) {
      tagging.enterEditMode(rowId)
    } else if (isEditMode && isChecked && !isActiveEditorRow) {
      tagging.setActiveEditorRowId(rowId)
    }
  }

  const handleOverflowClick = () => {
    tagging.enterEditMode(rowId)
  }

  const handleRemoveTag = (tag: Tag) => {
    const targetIds =
      isEditMode && isChecked ? selection.selectedRowIds : [rowId]

    tagging.removeTag(targetIds, tag)
  }

  if (isReadMode) {
    return (
      <div
        className="TagCell"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!hasTags && (
          <>
            <span className="TagCell-empty">
              {intl.formatMessage({ id: 'Tags_no_tags' })}
            </span>

            <AddTagButton
              focusableWhenHidden
              onClick={handleAddClick}
              visible={isHovered}
            />
          </>
        )}

        {hasTags && (
          <div ref={containerRef} className="TagCell-tags">
            {(isMeasuring ? tags : tags.slice(0, visibleCount)).map(tag => (
              <TagBadge
                key={tag.id}
                color={tag.color}
                inherited={tag.inherited}
                inheritedFrom={tag.inheritedFrom}
                name={tag.name}
                onNameClick={handleTagNameClick}
                onRemove={() => handleRemoveTag(tag)}
                showRemove={false}
              />
            ))}

            <TagOverflow
              count={isMeasuring ? 0 : tags.length - visibleCount}
              onClick={handleOverflowClick}
            />

            <AddTagButton onClick={handleAddClick} visible={isHovered} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="TagCell TagCell--edit">
      <div className="TagCell-tags">
        {isActiveEditorRow && (
          <div className="TagEditor-anchor">
            <TagEditor
              ariaLabel={intl.formatMessage({ id: 'Tags_add_tag_input' })}
              disabled={tagging.isAddingTag || tagging.isRemovingTag}
              onExit={tagging.exitEditMode}
            />
            <EditorDropdown />
          </div>
        )}

        {showMirror && <TagMirror />}

        {!hasTags && !isActiveEditorRow && (
          <span className="TagCell-empty">
            {intl.formatMessage({ id: 'Tags_no_tags' })}
          </span>
        )}

        {tags.map(tag => (
          <TagBadge
            key={tag.id}
            color={tag.color}
            disableRemove={tagging.isRemovingTag || tagging.isAddingTag}
            inherited={tag.inherited}
            inheritedFrom={tag.inheritedFrom}
            name={tag.name}
            onNameClick={handleTagNameClick}
            onRemove={() => handleRemoveTag(tag)}
            showRemove
          />
        ))}
      </div>
    </div>
  )
}
